/**
 * The one appearance of a registration token, and the copy button that must never
 * fail quietly.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THIS COMPONENT CANNOT RENDER A SPENT TOKEN, AND NOT BY CONVENTION.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * It takes a `Reveal`. The plaintext is a member of the `shown` variant and of no
 * other, so the `spent` branch below has nothing to read: `state.token` does not
 * type-check there. `web/src/devices/reveal.ts` explains the rest of the structure —
 * `burn` is total and has no inverse, and `RegistrationTokenSummary` (the list row)
 * declares no `token`, so there is no re-fetch that could produce one either.
 *
 * ── The copy failure is the interesting case ────────────────────────────────
 *
 * `navigator.clipboard` is **undefined** in an insecure context — plain `http://` on
 * anything but localhost, which is exactly how a first on-premise install is reached
 * — and `writeText` **rejects** when the permission is refused or the document is not
 * focused. Both are ordinary and both are silent, and a silent failure here is the
 * worst outcome on the whole screen: the operator believes they have the token,
 * navigates away, and the plaintext ceases to exist.
 *
 * So the outcome is rendered, the two failures are **kept apart** — "this page is not
 * allowed to use the clipboard at all" and "the copy was refused just now" have
 * different remedies — and both failure notes are `high` severity with `role="alert"`,
 * say in as many words that the token is still on screen *right now*, and tell the
 * reader to write it down. The token stays selectable underneath either way; the copy
 * button is a convenience over the rendered value, never the only path to it.
 *
 * ── What is deliberately absent ─────────────────────────────────────────────
 *
 * No "show again", no "reveal", no storage of any kind, and the enrolment URL is a
 * separate field rather than a link carrying the token — a URL with a bearer
 * credential in it ends up in browser history, in a `Referer` and in a proxy access
 * log, which is why the server does not put it there and why this screen does not
 * assemble one.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { copyToClipboard, type CopyOutcome, type Reveal } from '../../devices/reveal';
import { Icon, SEVERITY_ICON } from '../../states/icons';
import { StatusNote } from '../../states/StatusNote';

export function TokenReveal({
  state,
  onDismiss,
  /**
   * Injected so both failure branches are reachable in a test without deleting a
   * global. jsdom ships no clipboard and neither does a browser on `http://`; a
   * component that reached for `navigator.clipboard` directly could only ever be
   * tested on the branch that does not matter.
   */
  clipboard,
}: {
  readonly state: Reveal;
  readonly onDismiss: () => void;
  readonly clipboard?: Pick<Clipboard, 'writeText'> | undefined;
}): ReactNode {
  const { t } = useI18n();
  const [copy, setCopy] = useState<CopyOutcome>('idle');
  const headingRef = useRef<HTMLParagraphElement | null>(null);

  // Focus moves to the panel the moment it appears: the token is the thing the
  // operator came for and it is about to be unavailable for ever.
  useEffect(() => {
    if (state.phase === 'shown') headingRef.current?.focus();
  }, [state.phase]);

  if (state.phase === 'none') return null;

  if (state.phase === 'spent') {
    // Nothing readable here holds a secret: `RevealSpent` carries the id and the
    // prefix, and `openapi.yaml` says the prefix is "drawn from its own random source
    // at mint time" and "carries no part of the secret".
    return (
      <StatusNote
        severity="info"
        title={t('devices.tokens.spent.title', { prefix: state.tokenPrefix })}
        testId="k5-token-spent"
      >
        <p>{t('devices.tokens.spent.body')}</p>
      </StatusNote>
    );
  }

  /*
   * The slip, and why it is a slip.
   *
   * This is the one value in the product that exists on screen and nowhere else: a
   * plaintext that is never stored, never re-fetched and never shown twice. Drawn as
   * one more `StatusNote` it would look like the eight other notes on this screen —
   * the same banner the fleet uses to say a read was late — and the operator would
   * scroll past the only chance they get.
   *
   * So it is drawn as what it is: a slip of paper torn off the page. A perforation
   * across the top, the secret set in mono large enough to read across a desk and
   * selectable in one gesture, and one 44px copy control beside the dismissal that
   * burns it. High severity in the same wash-and-1px vocabulary as everything else —
   * the border is the severity colour on all four sides, never a stripe down one.
   *
   * The two copy failures stay `StatusNote`s and stay **outside** the slip: they are
   * states that arrived, they are not part of the slip, and a note inside a note is
   * the nested-box mistake DESIGN.md §12 names.
   */
  return (
    <>
      <section
        className="wp-c-slip"
        role="alert"
        data-severity="high"
        data-testid="k5-token-reveal"
      >
        <p className="wp-c-slip__key">
          <Icon name={SEVERITY_ICON.high} className="wp-c-slip__icon" />
          {t('devices.tokens.reveal.title')}
        </p>

        <p
          className="wp-c-slip__warning"
          ref={headingRef}
          tabIndex={-1}
          data-testid="k5-token-warning"
        >
          {t('devices.tokens.reveal.warning')}
        </p>

        <div className="wp-c-slip__field">
          <span className="wp-label">{t('devices.tokens.reveal.token')}</span>
          {/* Selectable text, so the operator has a path to the value that does not
              depend on a clipboard API working. */}
          <p className="wp-c-slip__secret" data-testid="k5-token-plaintext">
            {state.token}
          </p>
        </div>

        <div className="wp-c-slip__field">
          <span className="wp-label">{t('devices.tokens.reveal.url')}</span>
          <p className="wp-c-slip__url" data-testid="k5-token-url">
            {state.enrollmentUrl ?? (
              <span className="wp-muted">{t('devices.tokens.reveal.urlMissing')}</span>
            )}
          </p>
        </div>

        <div className="wp-c-actions">
          <button
            type="button"
            className="wp-button wp-button--primary"
            data-testid="k5-token-copy"
            onClick={() => {
              void copyToClipboard(state.token, clipboard).then(setCopy);
            }}
          >
            {t('devices.tokens.reveal.copy')}
          </button>
          <button
            type="button"
            className="wp-button wp-button--quiet"
            data-testid="k5-token-dismiss"
            onClick={onDismiss}
          >
            {t('devices.tokens.reveal.dismiss')}
          </button>
        </div>

        {copy === 'copied' ? (
          <p className="wp-small" data-testid="k5-token-copied">
            {t('devices.tokens.reveal.copied')}
          </p>
        ) : null}
      </section>

      {/* Two failures, two sentences, both loud. Never a no-op. */}
      {copy === 'failed' ? (
        <StatusNote
          severity="high"
          role="alert"
          title={t('devices.tokens.reveal.copyFailed')}
          testId="k5-token-copy-failed"
        />
      ) : null}
      {copy === 'unavailable' ? (
        <StatusNote
          severity="high"
          role="alert"
          title={t('devices.tokens.reveal.copyUnavailable')}
          testId="k5-token-copy-unavailable"
        />
      ) : null}
    </>
  );
}
